import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Star, Flame, Trophy, Zap, Award } from "lucide-react";
import api from "@/config/api";
import { useAuth } from "@/contexts/AuthContext";
import { BadgeItem } from "@/components/BadgeItem";
import { StatCard } from "@/components/StatCard";
import { Loading } from "@/components/Feedback";
import { useSeo } from "@/hooks/useSeo";

export default function Dashboard() {
  useSeo("Tableau de bord", "Tes récompenses Quran Coach : points, étoiles, séries, badges débloqués et historique de tes récitations récentes.");
  const { user } = useAuth();
  const { data: gam, isLoading } = useQuery({ queryKey: ["gamification"], queryFn: async () => (await api.get("/gamification")).data });
  const { data: recit } = useQuery({ queryKey: ["recit-history"], queryFn: async () => (await api.get("/recitation/history")).data });

  if (isLoading) return <Loading full />;

  const earned = gam.badges.filter((b) => b.earned).length;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-black text-slate-800">Tableau de bord</h1>
        <p className="text-slate-500">Tes récompenses et ton activité récente, {user?.first_name}.</p>
      </div>

      {/* Hero gamification card */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden bg-gradient-to-br from-accent to-yellow-400 rounded-3xl p-6 sm:p-8 text-slate-900">
        <div className="absolute inset-0 islamic-star-pattern opacity-20" />
        <div className="relative grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { icon: Zap, label: "Points", value: gam.points },
            { icon: Star, label: "Étoiles", value: gam.stars },
            { icon: Flame, label: "Série", value: `${gam.streak} j` },
            { icon: Trophy, label: "Record", value: `${gam.best_streak} j` },
          ].map((s) => (
            <div key={s.label} className="text-center">
              <s.icon className="w-7 h-7 mx-auto mb-1" />
              <div className="text-3xl font-black">{s.value}</div>
              <div className="text-xs font-bold uppercase tracking-wide opacity-70">{s.label}</div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Badges */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-black text-slate-800">Mes badges</h2>
          <span className="text-sm font-bold text-slate-400">{earned}/{gam.badges.length} débloqués</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {gam.badges.map((b) => <BadgeItem key={b.id} badge={b} />)}
        </div>
      </section>

      {/* Recent recitations */}
      <section>
        <h2 className="font-black text-slate-800 mb-4">Récitations récentes</h2>
        {!recit?.length ? (
          <div className="bg-white p-6 rounded-3xl border border-slate-100 text-center text-slate-400 font-semibold" data-testid="no-recitations">
            Aucune récitation pour l'instant. Va dans l'onglet Réciter pour commencer !
          </div>
        ) : (
          <div className="space-y-3">
            {recit.slice(0, 6).map((r) => (
              <div key={r.id} className="flex items-center gap-4 bg-white p-4 rounded-2xl border border-slate-100" data-testid={`recit-${r.id}`}>
                <div className="w-11 h-11 rounded-2xl bg-primary/10 text-primary flex items-center justify-center"><Award className="w-5 h-5" /></div>
                <div className="flex-1">
                  <div className="font-extrabold text-slate-700 text-sm">Sourate {r.surah_number} · verset {r.ayah_number}</div>
                  <div className="text-xs text-slate-400">{new Date(r.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</div>
                </div>
                <span className={`font-black text-lg ${r.score >= 85 ? "text-emerald-600" : "text-yellow-600"}`}>{r.score}%</span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
